import { useNavigate, useParams } from '@tanstack/react-router';
import { CheckCircle, Loader2, UserCheck, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { approveUserByToken } from '@/lib/auth/security-service';

type ApprovalState = 'processing' | 'success' | 'alreadyApproved' | 'error';

export default function ApproveAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams({ strict: false });

  const [state, setState] = useState<ApprovalState>('processing');

  useEffect(() => {
    const approveUser = async () => {
      if (!token) {
        setState('error');
        return;
      }

      const result = await approveUserByToken(token);

      if (result.success) {
        if (result.data.alreadyApproved) {
          setState('alreadyApproved');
        } else {
          setState('success');
        }
      } else {
        setState('error');
      }
    };

    approveUser();
  }, [token]);

  const handleNavigate = () => {
    navigate({ to: '/dashboard' });
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-xl bg-surface-card p-8 text-center shadow-lg">
        {state === 'processing' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-accent-primary" />
            <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-primary">
              {t('approval.title')}
            </h1>
            <p className="mt-2 text-foreground-secondary">
              {t('approval.processing')}
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-primary">
              {t('approval.success')}
            </h1>
            <p className="mt-2 text-foreground-secondary">
              {t('approval.successMessage')}
            </p>
            <button
              type="button"
              onClick={handleNavigate}
              className="mt-6 rounded-lg bg-accent-primary px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
            >
              {t('approval.goToDashboard')}
            </button>
          </>
        )}

        {state === 'alreadyApproved' && (
          <>
            <UserCheck className="mx-auto h-12 w-12 text-blue-500" />
            <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-primary">
              {t('approval.alreadyApproved')}
            </h1>
            <p className="mt-2 text-foreground-secondary">
              {t('approval.alreadyApprovedMessage')}
            </p>
            <button
              type="button"
              onClick={handleNavigate}
              className="mt-6 rounded-lg bg-accent-primary px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
            >
              {t('approval.goToDashboard')}
            </button>
          </>
        )}

        {state === 'error' && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-red-500" />
            <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-primary">
              {t('approval.error')}
            </h1>
            <p className="mt-2 text-foreground-secondary">
              {t('approval.errorMessage')}
            </p>
            <button
              type="button"
              onClick={handleNavigate}
              className="mt-6 rounded-lg bg-accent-primary px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-hover"
            >
              {t('approval.goToDashboard')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
